import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDescope } from '@descope/react-sdk';
import { useAlert } from 'context/AlertContext';
import { errorTransformer, ErrorInput } from 'utils/errorTransformer';
import { LoginLayout } from 'components/organisms';
import ResetPasswordForm from 'components/organisms/ResetPasswordForm';
import { ResetPasswordFormData } from 'types/auth.types';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sdk = useDescope();
  const { showSuccess, showError } = useAlert();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [tokenLoading, setTokenLoading] = useState<boolean>(true);
  const [tokenValid, setTokenValid] = useState<boolean>(false);
  const [loginId, setLoginId] = useState<string>('');

  useEffect(() => {
    const token = searchParams.get('t');

    if (!token) {
      setTokenValid(false);
      setTokenLoading(false);
      return;
    }

    const exchangeToken = async (): Promise<void> => {
      try {
        const { ok, data, error: sdkError } = await sdk.magicLink.verify(token);

        if (!ok || sdkError) {
          setTokenValid(false);
          return;
        }

        const userLoginId = data?.user?.loginIds?.[0] ?? data?.user?.email ?? '';
        setLoginId(userLoginId);
        setTokenValid(true);
      } catch {
        setTokenValid(false);
      } finally {
        setTokenLoading(false);
      }
    };

    exchangeToken();
  }, [sdk, searchParams]);

  const handleSubmit = async (formData: ResetPasswordFormData): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const { ok, error: sdkError } = await sdk.password.update(loginId, formData.newPassword);

      if (!ok || sdkError) {
        throw sdkError || new Error('Failed to update password');
      }

      const successMessage = 'Your password has been updated successfully.';
      setSuccess(successMessage);
      showSuccess(successMessage, { duration: 5000 });

      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      const friendlyMessage = errorTransformer(err as ErrorInput);
      setError(friendlyMessage);
      showError(friendlyMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginLayout>
      <ResetPasswordForm
        onSubmit={handleSubmit}
        onBackToLogin={() => navigate('/login')}
        loading={loading}
        error={error}
        success={success}
        tokenValid={tokenValid}
        tokenLoading={tokenLoading}
      />
    </LoginLayout>
  );
};

export default ResetPassword;
